import React from "react";
import { Box, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { IconType } from "react-icons";
import { alpha, useGovTheme } from "./useGovTheme";
import { useCountUp } from "./motion";
import { GovSurface } from "./GovSurface";
import { GovTip } from "./GovTip";

interface Props {
  label: string;
  value: number;
  /** One short line under the number — what it means or what to do. */
  hint?: string;
  icon: IconType;
  tone?: "accent" | "success" | "warning" | "danger" | "neutral";
  /** What the number counts, for the tooltip. */
  tip: string;
  suffix?: string;
}

/** A number that counts up to its value, with an icon tile in its tone. The value is always the real count. */
export function GovStatCard({ label, value, hint, icon, tone = "accent", tip, suffix }: Props) {
  const t = useGovTheme();
  const shown = useCountUp(value);
  const tint = { accent: t.primary, success: t.success, warning: t.warning, danger: t.danger, neutral: t.neutral }[tone];
  return (
    <GovTip label={tip}>
      <Box>
        <GovSurface p={4} h="100%">
          <HStack justify="space-between" align="start" spacing={3}>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="semibold" color={t.muted} noOfLines={1}>{label}</Text>
              <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="-0.03em" color={t.text} lineHeight="1.1" mt={1} aria-label={`${value}${suffix ?? ""}`}>
                {shown}{suffix}
              </Text>
              {hint && <Text fontSize="sm" color={t.muted} mt={1} noOfLines={2}>{hint}</Text>}
            </Box>
            <Flex flexShrink={0} w={9} h={9} borderRadius="lg" align="center" justify="center" bg={alpha(tint, 0.14)}>
              <Icon as={icon} color={tint} boxSize={4} />
            </Flex>
          </HStack>
        </GovSurface>
      </Box>
    </GovTip>
  );
}
